import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PhoneIcon from '@mui/icons-material/Phone';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { CUSTOMERS } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { CUSTOMERS_LIST_PATH } from '../../navigations/path';
import { useAuth } from '../../hooks/useAuth';
import { CUSTOMER_PERMISSIONS } from './customerPermissions';
import { buildCustomerFormData } from './customerApiHelpers';
import { getLeadsStatus } from '../../store/slices/leadsSlice';

const genderOptions = [
  { value: '', label: 'Not Specified' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

const capitalizeWords = (text) => {
  if (!text) return '';
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
};

const generateSecurePassword = (length = 12) => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';
  const ensureChars = [
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)],
    'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)],
    '0123456789'[Math.floor(Math.random() * 10)],
    '!@#$%^&*()_+~`|}{[]:;?><,./-='[Math.floor(Math.random() * 20)],
  ];
  const array = new Uint8Array(length - ensureChars.length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  } else {
    for (let i = 0; i < array.length; i += 1) array[i] = Math.floor(Math.random() * 256);
  }
  let password = '';
  for (let i = 0; i < array.length; i += 1) password += chars[array[i] % chars.length];
  return (ensureChars.join('') + password)
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('');
};

const CreateCustomer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id: routeId } = useParams();
  const { hasPermission } = useAuth();

  const isViewMode = location.pathname.includes('/view/');
  const isEditMode = Boolean(routeId) && !isViewMode;
  const isCreateMode = !routeId;
  const customerId = routeId;

  const canView = hasPermission(CUSTOMER_PERMISSIONS.VIEW);
  const canEdit = hasPermission(CUSTOMER_PERMISSIONS.EDIT);
  const canCreate = hasPermission(CUSTOMER_PERMISSIONS.CREATE);

  const generated = generateSecurePassword();
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: isCreateMode ? generated : '',
    password_confirmation: isCreateMode ? generated : '',
    phone: '',
    gender: '',
    dob: '',
    status_id: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(customerId));
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await dispatch(
          getLeadsStatus({ 'conditions[model]': 'Customer' })
        ).unwrap();
        setStatuses(extractList(data));
      } catch {
        toast.error('Failed to load statuses.');
      }

      if (!customerId) return;
      if (isViewMode && !canView) {
        setIsLoading(false);
        return;
      }
      if (isEditMode && !canEdit) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        let customer;
        try {
          const { data } = await api.get(CUSTOMERS.BY_ID(customerId), {
            params: { 'relations[]': ['status', 'teams'] },
          });
          customer = data?.data ?? data;
        } catch {
          const { data } = await api.get(`/users/${customerId}`, {
            params: { 'relations[]': ['status', 'teams'] },
          });
          customer = data?.data ?? data;
        }

        const statusId =
          (customer?.status && typeof customer.status === 'object' && customer.status.id) ||
          customer?.status_id ||
          '';

        setFormData({
          name: customer?.name ?? '',
          username: customer?.username ?? '',
          email: customer?.email ?? '',
          password: '',
          password_confirmation: '',
          phone: customer?.phone ?? '',
          gender: customer?.gender ?? '',
          dob: customer?.dob ? String(customer.dob).slice(0, 10) : '',
          status_id: statusId,
        });
      } catch (err) {
        console.error(err);
        toast.error('Failed to load customer.');
        navigate(CUSTOMERS_LIST_PATH);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [customerId, canView, canEdit, isEditMode, isViewMode, navigate, dispatch]);

  const validateForm = (data) => {
    const validationErrors = {};
    if (!data.name.trim()) validationErrors.name = 'Full name is required.';
    if (!data.username.trim()) validationErrors.username = 'Username is required.';
    else if (!/^[a-zA-Z0-9_]{3,15}$/.test(data.username)) {
      validationErrors.username = 'Username must be 3-15 alphanumeric characters or underscores.';
    }
    if (!data.email.trim()) validationErrors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      validationErrors.email = 'Invalid email format.';
    }
    if (isCreateMode && !data.password.trim()) {
      validationErrors.password = 'Password is required.';
    }
    if (!data.status_id) validationErrors.status_id = 'Status is required.';
    return validationErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, '') }));
    } else if (name === 'password') {
      setFormData((prev) => ({
        ...prev,
        password: value,
        password_confirmation: value,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleGeneratePassword = () => {
    const next = generateSecurePassword();
    setFormData((prev) => ({
      ...prev,
      password: next,
      password_confirmation: next,
    }));
    setErrors((prev) => ({ ...prev, password: '', password_confirmation: '' }));
  };

  const handleCopyPassword = () => {
    if (!formData.password) return;
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(formData.password)
        .then(() => setSnackbarOpen(true))
        .catch(() => toast.error('Failed to copy password.'));
    } else {
      setSnackbarOpen(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewMode) return;
    if (isCreateMode && !canCreate) return;
    if (isEditMode && !canEdit) return;

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      toast.error('Please fix the errors in the form.');
      return;
    }

    setIsSubmitting(true);
    try {
      const includePassword = isCreateMode || Boolean(formData.password.trim());
      const payload = buildCustomerFormData(formData, { includePassword });

      if (isEditMode) {
        await api.post(`${CUSTOMERS.BY_ID(customerId)}?_method=PUT`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`Customer ${formData.name} updated successfully!`);
      } else {
        await api.post(CUSTOMERS.LIST, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`Customer ${formData.name} created successfully!`);
      }
      navigate(CUSTOMERS_LIST_PATH);
    } catch (error) {
      const data = error.response?.data;
      const fieldErrors = data?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const next = {};
        Object.entries(fieldErrors).forEach(([key, messages]) => {
          next[key] = Array.isArray(messages) ? messages[0] : String(messages);
          toast.error(next[key]);
        });
        setErrors((prev) => ({ ...prev, ...next }));
      } else {
        toast.error(data?.message || (isEditMode ? 'Failed to update customer.' : 'Failed to create customer.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      </PageLayout>
    );
  }

  const formTitle = isViewMode
    ? `View Customer: ${formData.name || ''}`
    : isEditMode
      ? `Edit Customer: ${formData.name || ''}`
      : 'Add Customer';
  const isFormDisabled = isViewMode || isSubmitting;

  return (
    <PageLayout>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, bgcolor: 'background.paper', minHeight: '100vh' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(CUSTOMERS_LIST_PATH)}
          sx={{ mb: 2 }}
        >
          Back to Customers
        </Button>

        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4 }}
        >
          {formTitle}
        </Typography>

        <Paper elevation={4} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, maxWidth: '900px', mx: 'auto' }}>
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{ mb: 3, borderBottom: 2, borderColor: 'divider', pb: 1, color: 'primary.main' }}
          >
            Customer Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Role is fixed to <strong>Customer</strong>.
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isFormDisabled}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={isFormDisabled}
                  error={!!errors.username}
                  helperText={errors.username || '3–15 alphanumeric characters or underscores.'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isFormDisabled}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AlternateEmailIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isFormDisabled}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="primary" />
                      </InputAdornment>
                    ),
                    inputProps: { maxLength: 15 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={genderOptions}
                  getOptionLabel={(option) => option.label}
                  value={genderOptions.find((g) => g.value === formData.gender) || null}
                  onChange={(_, newValue) =>
                    handleChange({
                      target: { name: 'gender', value: newValue ? newValue.value : '' },
                    })
                  }
                  disabled={isFormDisabled}
                  renderInput={(params) => <TextField {...params} label="Gender" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statuses}
                  getOptionLabel={(option) => capitalizeWords(option.name || '')}
                  value={statuses.find((s) => s.id === formData.status_id) || null}
                  onChange={(_, newValue) =>
                    handleChange({
                      target: { name: 'status_id', value: newValue ? newValue.id : '' },
                    })
                  }
                  disabled={isFormDisabled}
                  noOptionsText="No statuses available"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      error={!!errors.status_id}
                      helperText={errors.status_id}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {!isViewMode && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isEditMode ? 'New Password (optional)' : 'Password'}
                    name="password"
                    type="text"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isFormDisabled}
                    error={!!errors.password}
                    helperText={
                      errors.password ||
                      (isEditMode ? 'Leave blank to keep current password.' : '')
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKeyIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            onClick={handleGeneratePassword}
                            startIcon={<VisibilityOffIcon />}
                            color="primary"
                            size="small"
                            sx={{ mr: 1, whiteSpace: 'nowrap' }}
                            disabled={isFormDisabled}
                          >
                            Generate
                          </Button>
                          <IconButton
                            onClick={handleCopyPassword}
                            edge="end"
                            color="primary"
                            size="small"
                            disabled={isFormDisabled || !formData.password}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}

              {!isViewMode && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={isSubmitting ? null : <PersonAddIcon />}
                      disabled={isSubmitting}
                      sx={{ py: 1.5, px: 4, borderRadius: 2 }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : isEditMode ? (
                        'Update Customer'
                      ) : (
                        'Add Customer'
                      )}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </form>
        </Paper>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" sx={{ width: '100%' }} onClose={() => setSnackbarOpen(false)}>
            Password copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    </PageLayout>
  );
};

export default CreateCustomer;
